/**
 * Shared live-HTML state for in-flight webpage builds.
 *
 * Problem: useChat dispatches `xavior:html-config-update` CustomEvents as
 * the agent streams html.chunks, but HtmlViewerModule may not be mounted
 * yet when the first dispatch fires (PagesContainerModule opens it
 * reactively AFTER the module.focus action propagates). Without this map,
 * HtmlViewerModule on mount would have no content and would show the
 * building skeleton for multiple seconds until the next chunk's dispatch.
 *
 * Solution: useChat writes the latest HTML for each in-flight module into
 * this map on every chunk, and HtmlViewerModule reads from it on mount as
 * its initial `liveHtml` state. The event listener then takes over for
 * subsequent chunks.
 */

interface LiveHtmlEntry {
  html: string;
  final: boolean;
}

const liveHtmlByModuleId = new Map<string, LiveHtmlEntry>();

/**
 * Strip the transient `data-xavior-stream-enter` marker before persistence.
 * Kept in lockstep with useChat's stripStreamEnterAttr — any persisted HTML
 * must be free of the attribute so a reload doesn't render every section
 * invisibly if iframe-side stripping ever fails.
 */
function stripStreamEnterForFinal(html: string): string {
  if (!html) return html;
  return html.replace(/\s+data-xavior-stream-enter(?:="[^"]*")?/g, '');
}

export function setLiveHtml(moduleId: string, html: string, final = false): void {
  // Only strip at the persistence boundary. Intermediate in-flight state is
  // kept raw (the per-chunk handler used to strip on every call which was
  // O(n²) on large builds).
  const storedHtml = final ? stripStreamEnterForFinal(html) : html;
  liveHtmlByModuleId.set(moduleId, { html: storedHtml, final });
}

export function getLiveHtml(moduleId: string): LiveHtmlEntry | undefined {
  return liveHtmlByModuleId.get(moduleId);
}

export function clearLiveHtml(moduleId: string): void {
  liveHtmlByModuleId.delete(moduleId);
}
